import { useCallback, useState } from 'react';
import { Modal } from '../../../../components/modal/modal';
import { Button } from '../../../../components/form/button';
import { debounce } from 'lodash';
import { useYorentClient } from '../../../../clients/yorent/yorent-client';
import { toast } from '../../../../util/toasts';
import { Order } from '../../../../clients/quipli-service/order/order';
import { QrScanner } from '@yudiel/react-qr-scanner';
import { Spinner } from '../../../../components/spinner/spinner';

const QRScannerModal = ({
  scanQrModalOpen,
  setScanQrModalOpen,
  scanQRcode,
}: {
  scanQrModalOpen: {
    open: boolean;
    isManualEntry: boolean;
    manualEntry: string;
    isSubmitting: boolean;
  };
  setScanQrModalOpen: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      isManualEntry: boolean;
      manualEntry: string;
      isSubmitting: boolean;
    }>
  >;
  scanQRcode: (result: string) => void;
}) => {
  return (
    <Modal
      title="Scan Unit QR"
      isOpen={scanQrModalOpen.open}
      close={() => setScanQrModalOpen((prev) => ({ ...prev, open: false }))}
    >
      {scanQrModalOpen.isManualEntry ? (
        <div className="manual-qr-entry bg-white p-5 md:w-[500px] flex flex-col items-center" data-cy="manual-section">
          <div className="min-w-full h-[44px] p-[10px] flex items-center gap-3">
            <span className="quipli-v2 text-md">QR Code:</span>
            <input
              type="text"
              className="quipli-v2 text-md"
              data-cy="manual-entry"
              value={scanQrModalOpen.manualEntry}
              onChange={(e) => setScanQrModalOpen((prev) => ({ ...prev, manualEntry: e.target.value }))}
            />
          </div>

          {!scanQrModalOpen.isSubmitting && (
            <>
              <Button
                className="button button-primary mb-3"
                dataCy="submit"
                onClick={() => {
                  setScanQrModalOpen((prev) => ({ ...prev, isSubmitting: true }));
                  scanQRcode('manager/orders/inventoryunit?qrcode=' + scanQrModalOpen.manualEntry);
                }}
              >
                Submit
              </Button>

              <Button
                className="button button-secondary"
                dataCy="qr-scanner"
                onClick={() => {
                  setScanQrModalOpen((prev) => ({ ...prev, isManualEntry: false, manualEntry: '' }));
                }}
              >
                QR Scanner
              </Button>
            </>
          )}
        </div>
      ) : (
        <div data-cy="scan-section" className="qr-scanner bg-white p-5 md:w-[500px] flex flex-col items-center">
          {!scanQrModalOpen.isSubmitting && (
            <>
              <QrScanner
                onDecode={(result) => {
                  if (!scanQrModalOpen.isSubmitting) {
                    setScanQrModalOpen((prev) => ({ ...prev, isSubmitting: true }));
                    scanQRcode(result);
                  } else {
                    console.log('Ignoring extra decode');
                  }
                }}
                onError={() => {}}
              />

              <Button
                className="button button-secondary"
                data-cy="manual-scan"
                onClick={() => {
                  setScanQrModalOpen((prev) => ({ ...prev, isManualEntry: true }));
                }}
              >
                Enter Manually
              </Button>
            </>
          )}
        </div>
      )}

      {scanQrModalOpen.isSubmitting && <Spinner />}
    </Modal>
  );
};

export const UseScanQRModal = ({
  order,
  refresh,
  setHourReadingModalOpen,
}: {
  order: Order;
  refresh: () => void;
  setHourReadingModalOpen: (modal: {
    open: boolean;
    productId: number;
    unitId: number;
    unitName: string;
    hour: number;
  }) => void;
}) => {
  const { assignUnitFromQRCodeLegacy } = useYorentClient();

  const [scanQrModalOpen, setScanQrModalOpen] = useState({
    open: false,
    isManualEntry: false,
    manualEntry: '',
    isSubmitting: false,
  });

  const scanQRcode = useCallback(
    debounce((result) => {
      if (order && result.toLocaleLowerCase().includes('manager/orders/inventoryunit?qrcode=')) {
        const unitQRId = result.substring(result.lastIndexOf('=') + 1);

        assignUnitFromQRCodeLegacy(order.id || 0, unitQRId)
          .then((res) => {
            if (res.status === 1) {
              if (res.addHourReading === 1 && res.label && res.unitId) {
                res.msg && toast(<div dangerouslySetInnerHTML={{ __html: res.msg }} />, 'error');

                setHourReadingModalOpen({
                  open: true,
                  productId: res.productId,
                  unitId: res.unitId,
                  unitName: res.label,
                  hour: res.lastHourReading,
                });
              } else {
                refresh();
              }
            } else {
              res.msg && toast(<div dangerouslySetInnerHTML={{ __html: res.msg }} />, 'error');
            }
          })
          .catch((err) => {
            console.error(err);
            toast('Error assigning units', 'error');
          })
          .finally(() => {
            setScanQrModalOpen((prev) => ({
              ...prev,
              open: false,
              // isSubmitting: false, do not set to false, we don't want to show the scanner for a few milliseconds before closing
            }));
          });
      }
    }, 600),
    [order],
  );

  return {
    QRScannerModal: (
      <QRScannerModal
        scanQrModalOpen={scanQrModalOpen}
        setScanQrModalOpen={setScanQrModalOpen}
        scanQRcode={scanQRcode}
      />
    ),
    scanQrModalOpen,
    setScanQrModalOpen,
  };
};
